import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';
import { showToast } from '../../components/utils/Toast';

export const fetchEvents = createAsyncThunk(
  'events/fetchEvents',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/events`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch events');
      }

      return data.events;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const createEvent = createAsyncThunk(
  'events/createEvent',
  async (eventData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventData)
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to create event');
      }

      return data.event;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const updateEvent = createAsyncThunk(
  'events/updateEvent',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/events/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update event');
      }

      return data.event;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const deleteEvent = createAsyncThunk(
  'events/deleteEvent',
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/events/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete event');
      }

      if (Array.isArray(data.deletedIds) && data.deletedIds.length > 0) {
        return data.deletedIds;
      }
      return [id];
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const duplicateEvent = createAsyncThunk(
  'events/duplicate',
  async ({ id, start }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiUrl}/api/events/${id}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ start })
      });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Failed to duplicate event');
      return data.event;
    } catch (e) { return rejectWithValue(e.message); }
  }
);

export const expandRecurrence = createAsyncThunk(
  'events/expand',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiUrl}/api/events/${id}/expand`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Failed to expand');
      await dispatch(fetchEvents());
      return data;
    } catch (e) { return rejectWithValue(e.message); }
  }
);

export const fetchProjectQueue = createAsyncThunk(
  'events/fetchQueue',
  async (status, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      const res = await fetch(`${config.apiUrl}/api/events/queue${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Failed to load queue');
      return data.queue;
    } catch (e) { return rejectWithValue(e.message); }
  }
);

export const sendTomorrowDigest = createAsyncThunk(
  'events/sendDigest',
  async (recipientEmail, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiUrl}/api/events/digest/tomorrow`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(recipientEmail ? { recipientEmail } : {}),
      });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Failed to send digest');
      return data;
    } catch (e) { return rejectWithValue(e.message); }
  }
);

const eventsSlice = createSlice({
  name: 'events',
  initialState: {
    events: [],
    queue: [],
    loading: false,
    queueLoading: false,
    error: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.events.push(action.payload);
        showToast('Event created successfully', 'success');
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        const index = state.events.findIndex(event => event._id === action.payload._id);
        if (index !== -1) {
          state.events[index] = action.payload;
        }
        showToast('Event updated successfully', 'success');
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        const ids = new Set(Array.isArray(action.payload) ? action.payload : [action.payload]);
        state.events = state.events.filter((event) => !ids.has(event._id));
        showToast(ids.size > 1 ? 'Recurring event deleted successfully' : 'Event deleted successfully', 'success');
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(duplicateEvent.fulfilled, (state, action) => {
        state.events.push(action.payload);
        showToast('Event duplicated', 'success');
      })
      .addCase(duplicateEvent.rejected, (_, action) => {
        showToast(action.payload, 'error');
      })
      .addCase(fetchProjectQueue.pending, (state) => { state.queueLoading = true; })
      .addCase(fetchProjectQueue.fulfilled, (state, action) => {
        state.queueLoading = false;
        state.queue = action.payload;
      })
      .addCase(fetchProjectQueue.rejected, (state, action) => {
        state.queueLoading = false;
        showToast(action.payload, 'error');
      })
      .addCase(sendTomorrowDigest.fulfilled, (_, action) => {
        showToast(`Digest sent (${action.payload.sent || 0})`, 'success');
      })
      .addCase(sendTomorrowDigest.rejected, (_, action) => {
        showToast(action.payload, 'error');
      });
  }
});

export const { clearError } = eventsSlice.actions;
export default eventsSlice.reducer; 